import React, { useEffect, useState } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { api } from "@/lib/api";
import { formatDuration, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Plus, Folder as FolderIcon, List as ListIcon, ArrowRight, Clock } from "lucide-react";
import CreateFolderDialog from "@/components/dialogs/CreateFolderDialog";
import CreateWorkspaceDialog from "@/components/dialogs/CreateWorkspaceDialog";
import { Link } from "react-router-dom";

export default function DashboardPage() {
  const { activeWorkspace, tree } = useWorkspace();
  const [summary, setSummary] = useState(null);
  const [openFolder, setOpenFolder] = useState(false);
  const [openWorkspace, setOpenWorkspace] = useState(false);

  useEffect(() => {
    if (!activeWorkspace) { setSummary(null); return; }
    api.get(`/workspaces/${activeWorkspace.id}/time-summary`).then(r => setSummary(r.data)).catch(() => {});
  }, [activeWorkspace, tree]);

  if (!activeWorkspace) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white border border-neutral-200 p-10 rounded-sm" data-testid="no-workspace-empty">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">No workspace</div>
          <h1 className="font-display text-3xl font-bold mb-2">Create your first workspace</h1>
          <p className="text-sm text-neutral-500 mb-6">Workspaces hold your folders, lists, and access rules.</p>
          <Button onClick={() => setOpenWorkspace(true)} className="rounded-sm bg-neutral-950 hover:bg-neutral-800" data-testid="dashboard-create-workspace">
            <Plus className="w-4 h-4 mr-1" /> New workspace
          </Button>
          <CreateWorkspaceDialog open={openWorkspace} onOpenChange={setOpenWorkspace} />
        </div>
      </div>
    );
  }

  const allLists = tree.folders.flatMap(f => (tree.listsByFolder[f.id] || []).map(l => ({ ...l, _folder: f.name })));
  const folderTotals = new Map((summary?.by_folder || []).map(r => [r.folder_id, r.total_seconds]));
  const listTotals = new Map((summary?.by_list || []).map(r => [r.list_id, r.total_seconds]));

  return (
    <div data-testid="dashboard-page">
      <header className="h-14 border-b border-neutral-200 flex items-center px-6 bg-white/80 backdrop-blur-xl sticky top-0 z-20">
        <div>
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Workspace</div>
          <h1 className="font-display text-base font-bold leading-none">{activeWorkspace.name}</h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 hidden sm:block">My role</div>
          <span className="text-xs font-semibold uppercase tracking-wider px-2 py-1 border border-neutral-300 rounded-full">
            {activeWorkspace.my_role}
          </span>
        </div>
      </header>

      <div className="p-6 space-y-6 max-w-6xl">
        {/* Hero */}
        <section className="grid lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200 rounded-sm overflow-hidden">
          <div className="bg-white p-6">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Total time</div>
            <div className="font-mono-data text-3xl font-bold">{formatDuration(summary?.total_seconds || 0)}</div>
            <div className="text-xs text-neutral-500 mt-1">All tracked work across the workspace</div>
          </div>
          <div className="bg-white p-6">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Folders</div>
            <div className="font-mono-data text-3xl font-bold">{tree.folders.length}</div>
            <div className="text-xs text-neutral-500 mt-1">Organising your work</div>
          </div>
          <div className="bg-white p-6">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Lists</div>
            <div className="font-mono-data text-3xl font-bold">{allLists.length}</div>
            <div className="text-xs text-neutral-500 mt-1">Active boards under folders</div>
          </div>
        </section>

        {/* Folders */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold tracking-tight">Folders</h2>
            {["admin", "member"].includes(activeWorkspace.my_role) && (
              <Button onClick={() => setOpenFolder(true)} variant="outline" className="rounded-sm h-8" data-testid="dashboard-create-folder">
                <Plus className="w-3.5 h-3.5 mr-1" /> New folder
              </Button>
            )}
          </div>

          {tree.folders.length === 0 ? (
            <div className="border border-dashed border-neutral-300 rounded-sm p-10 text-center bg-white" data-testid="dashboard-empty-folders">
              <FolderIcon className="w-8 h-8 mx-auto text-neutral-400 mb-3" />
              <div className="text-sm text-neutral-600 mb-3">No folders yet. Start by creating one.</div>
              {["admin", "member"].includes(activeWorkspace.my_role) && (
                <Button onClick={() => setOpenFolder(true)} className="rounded-sm bg-neutral-950 hover:bg-neutral-800">
                  <Plus className="w-4 h-4 mr-1" /> Create folder
                </Button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {tree.folders.map((f) => {
                const lists = tree.listsByFolder[f.id] || [];
                const total = folderTotals.get(f.id) || 0;
                return (
                  <div key={f.id} className="bg-white border border-neutral-200 rounded-sm" data-testid={`folder-card-${f.id}`}>
                    <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FolderIcon className="w-4 h-4 text-neutral-700" />
                        <span className="font-semibold">{f.name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 font-mono-data text-xs text-neutral-600">
                        <Clock className="w-3 h-3" />{formatDuration(total)}
                      </div>
                    </div>
                    <div className="divide-y divide-neutral-100">
                      {lists.length === 0 && (
                        <div className="px-4 py-4 text-xs text-neutral-500">No lists in this folder.</div>
                      )}
                      {lists.map((l) => (
                        <Link
                          key={l.id}
                          to={`/list/${l.id}`}
                          className="px-4 py-2.5 flex items-center justify-between hover:bg-neutral-50 transition-colors group"
                          data-testid={`dashboard-list-${l.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <ListIcon className="w-3.5 h-3.5 text-neutral-500" />
                            <span className="text-sm truncate">{l.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono-data text-xs text-neutral-500">{formatDuration(listTotals.get(l.id) || 0)}</span>
                            <ArrowRight className="w-3.5 h-3.5 text-neutral-400 group-hover:text-neutral-950 transition-colors" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <CreateFolderDialog open={openFolder} onOpenChange={setOpenFolder} />
    </div>
  );
}
